import { DateTime, IANAZone } from 'luxon';
import { OfficeHoursValidator } from './office-hours.js';
export const parseHHMM = (hhmm) => {
    const [hours, minutes] = hhmm.split(':');
    return Number(hours) * 60 + Number(minutes);
};
const DOW_BY_LUXON_WEEKDAY = [
    'MON',
    'TUE',
    'WED',
    'THU',
    'FRI',
    'SAT',
    'SUN',
];
const wallClockIn = (at, tz) => {
    const dateTime = DateTime.fromJSDate(at).setZone(tz);
    return {
        weekday: DOW_BY_LUXON_WEEKDAY[dateTime.weekday - 1],
        minuteOfDay: dateTime.hour * 60 + dateTime.minute,
    };
};
// Wraps midnight when end < start.
const inTimeWindow = (minute, start, end) => start <= end
    ? minute >= start && minute < end
    : minute >= start || minute < end;
const toDate = (value) => value === null ? null : value instanceof Date ? value : new Date(value);
// @db.Date round-trips as UTC-midnight Date or ISO string — extract yyyy-MM-dd.
const toCalendarDate = (value) => {
    if (value === null)
        return null;
    if (value instanceof Date) {
        return DateTime.fromJSDate(value, { zone: 'utc' }).toISODate();
    }
    return value.slice(0, 10);
};
// Calendar bounds resolve in entry.timezone (wall-clock), not UTC. An unknown
// zone falls back to UTC — an invalid DateTime fails every comparison.
const calendarZone = (entry) => entry.timezone && IANAZone.isValidZone(entry.timezone)
    ? entry.timezone
    : 'UTC';
// True once the whole end day is over in the entry's timezone.
export const hasScheduleEntryEnded = (entry, at) => {
    const endCalendar = toCalendarDate(entry.endDate);
    if (!endCalendar)
        return false;
    const zone = calendarZone(entry);
    const endBound = DateTime.fromISO(endCalendar, { zone }).endOf('day');
    return DateTime.fromJSDate(at).setZone(zone) > endBound;
};
const frequencyMatchesAt = (entry, at) => {
    const frequency = entry.recurrenceFrequency;
    if (!frequency || frequency === 'WEEKLY')
        return true;
    const anchor = toDate(entry.recurrenceAnchor);
    if (!anchor)
        return true;
    if (at < anchor)
        return false;
    if (frequency === 'BIWEEKLY') {
        const weeksSince = Math.floor(DateTime.fromJSDate(at).diff(DateTime.fromJSDate(anchor), 'weeks').weeks);
        return weeksSince % 2 === 0;
    }
    if (frequency === 'MONTHLY') {
        const target = entry.dayOfMonth;
        if (target === null)
            return false;
        const dateTime = DateTime.fromJSDate(at).setZone(entry.timezone ?? 'UTC');
        return target === -1
            ? dateTime.day === dateTime.daysInMonth
            : dateTime.day === target;
    }
    return true;
};
export const isOneOffSchedule = (entry) => entry.startDate !== null &&
    entry.endDate !== null &&
    (entry.daysOfWeek?.length ?? 0) === 0;
export const scheduleEntryMatchesAt = (entry, at, options = {}) => {
    const preset = entry.preset ?? 'CUSTOM';
    if (preset === 'ALWAYS')
        return true;
    if (preset === 'BUSINESS_HOURS' || preset === 'AFTER_HOURS') {
        const context = options.companyOfficeHours;
        if (!context)
            return false;
        const within = OfficeHoursValidator.isWithinBusinessHours(at.toISOString(), context.timezoneName, context.weeklySchedule);
        return preset === 'BUSINESS_HOURS' ? within : !within;
    }
    if (options.checkDateWindow) {
        const zone = calendarZone(entry);
        const startCalendar = toCalendarDate(entry.startDate);
        if (startCalendar) {
            const startBound = DateTime.fromISO(startCalendar, {
                zone,
            }).startOf('day');
            if (DateTime.fromJSDate(at).setZone(zone) < startBound)
                return false;
        }
        if (hasScheduleEntryEnded(entry, at))
            return false;
    }
    if (isOneOffSchedule(entry))
        return true;
    const isMonthly = entry.recurrenceFrequency === 'MONTHLY';
    if (!isMonthly && (entry.daysOfWeek?.length ?? 0) === 0)
        return true;
    const { weekday, minuteOfDay } = wallClockIn(at, entry.timezone ?? 'UTC');
    if (!isMonthly && !entry.daysOfWeek.includes(weekday))
        return false;
    if (entry.timeOfDayStart && entry.timeOfDayEnd) {
        const start = parseHHMM(entry.timeOfDayStart);
        const end = parseHHMM(entry.timeOfDayEnd);
        if (!inTimeWindow(minuteOfDay, start, end))
            return false;
    }
    return frequencyMatchesAt(entry, at);
};
//# sourceMappingURL=schedule-match.js.map